import { Badge, type BadgeProps } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { Heading } from '@ctw/shared/components/heading';
import {
  TelemetryBoundary,
  type TelemetryContext,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { NAVIGATION_STACK_TEST_ID } from '@ctw/shared/test/ids';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import { faArrowLeftLong } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Transition } from '@headlessui/react';
import {
  Fragment,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface NavigationStackView {
  id: string;
  label: string;
  title: string;
  subtitle?: string;
  badges?: Array<BadgeProps>;
  content: {
    body: (args: NavigationStackRenderArgs) => ReactNode;
    footer?: (args: NavigationStackRenderArgs) => ReactNode;
  };
}

export interface NavigationStackNavigationAction {
  type: 'push' | 'pop';
  view: NavigationStackView;
}

export type NavigationStackProps = {
  className?: ClassName;
  telemetryContext?: TelemetryContext;
  initialViews: NonEmptyArray<NavigationStackView>;
  onFirstViewBackButtonClick?: () => void;
};

export const NavigationStack = ({
  className,
  telemetryContext,
  initialViews,
  onFirstViewBackButtonClick,
}: NavigationStackProps) => {
  const footerContentRef = useRef<HTMLDivElement>(null);

  // For padding's sake, watch footer presence
  const { height: footerHeight } = useContainerQuery({ containerRef: footerContentRef });

  return (
    <TelemetryBoundary boundaryName="NavigationStack" initialContext={telemetryContext}>
      {() => (
        <NavigationStackProvider initialViews={initialViews}>
          {({ state }) => (
            <section
              data-testid={NAVIGATION_STACK_TEST_ID}
              aria-label="Navigation Stack"
              className={twx(
                'flex h-full max-h-full min-h-full w-full max-w-full flex-1 flex-col overflow-visible',
                className,
              )}
            >
              <header
                data-testid="navigation-stack-header"
                className={tw`absolute-children-with-height border-border-main border-b`}
              >
                {(state.previouslyActiveView
                  ? [state.previouslyActiveView, state.activeView]
                  : [state.activeView]
                ).map((view) => (
                  <Transition
                    key={view.id}
                    show={state.activeView.id === view.id}
                    as={Fragment}
                    enter="transition-all ease-in-out duration-700"
                    enterFrom={twx({
                      'opacity-0 translate-x-full': state.lastNavigationAction?.type === 'push',
                      'opacity-0 -translate-x-full': state.lastNavigationAction?.type === 'pop',
                    })}
                    enterTo="opacity-100 translate-x-none"
                    leave="[&>div]:overflow-y-hidden transition-all ease-in-out duration-700"
                    leaveFrom="opacity-100 translate-x-none"
                    leaveTo={twx({
                      'opacity-0 -translate-x-full': state.lastNavigationAction?.type === 'push',
                      'opacity-0 translate-x-full': state.lastNavigationAction?.type === 'pop',
                    })}
                    unmount={false}
                    appear={state.activeView.id === view.id}
                  >
                    <div
                      className={tw`relative flex h-full max-h-full w-full max-w-full flex-1 gap-2 overflow-hidden p-2.5`}
                    >
                      <div
                        className={twx(
                          'flex shrink-0 items-start justify-center overflow-hidden transition-all duration-300',
                          {
                            'w-5 opacity-100':
                              view.id === state.activeView.id && state.views.length > 1,
                            'w-0 opacity-0':
                              !onFirstViewBackButtonClick &&
                              (state.views.length <= 1 || view.id !== state.activeView.id),
                          },
                        )}
                      >
                        <Button
                          testId={
                            view.id === state.activeView.id
                              ? 'navigation-stack-back-button'
                              : undefined
                          }
                          type="button"
                          ariaLabel="Back"
                          variant="circular"
                          size="xs"
                          disabled={view.id !== state.activeView.id}
                          className={tw`z-navigation-sticky flex h-5 w-5 min-w-5 items-center justify-center bg-transparent p-2 text-content-main shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15 disabled:bg-transparent`}
                          onClick={() =>
                            initialViews[0].id === state.activeView.id && onFirstViewBackButtonClick
                              ? onFirstViewBackButtonClick()
                              : state.popView()
                          }
                        >
                          <FontAwesomeIcon
                            icon={faArrowLeftLong}
                            className={tw`h-4 w-4 text-content-subtle`}
                          />
                        </Button>
                      </div>
                      <div className={tw`flex w-full flex-col overflow-hidden`}>
                        <div className={tw`flex items-start justify-between overflow-hidden`}>
                          <div
                            data-testid={
                              view.id === state.activeView.id ? 'navigation-stack-label' : undefined
                            }
                            className={tw`h-18 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-content-subtle text-xs uppercase tracking-wide`}
                          >
                            {view.label}
                          </div>
                          <div
                            data-testid={
                              view.id === state.activeView.id
                                ? 'navigation-stack-badges'
                                : undefined
                            }
                            className={tw`flex flex-1 flex-wrap justify-end gap-2`}
                          >
                            {view.badges &&
                              view.badges.length > 0 &&
                              view.badges.map((badge) => (
                                <Badge key={badge.content} {...badge} size="sm" />
                              ))}
                          </div>
                        </div>
                        <Heading
                          testId={
                            view.id === state.activeView.id ? 'navigation-stack-title' : undefined
                          }
                          level="h2"
                          className={tw`!p-0 line-clamp-2 overflow-hidden text-ellipsis font-medium text-content-main text-lg leading-7`}
                        >
                          {view.title}
                        </Heading>
                        {view.subtitle && (
                          <div
                            data-testid={
                              view.id === state.activeView.id
                                ? 'navigation-stack-subtitle'
                                : undefined
                            }
                            className={tw`text-content-subtle text-sm tracking-wide`}
                          >
                            {view.subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                  </Transition>
                ))}
              </header>
              <div
                className={tw`relative flex h-full max-h-full w-full max-w-full flex-col overflow-hidden`}
              >
                {(state.lastNavigationAction?.type === 'pop'
                  ? [...state.views, state.lastNavigationAction.view]
                  : state.views
                ).map((view) => (
                  <Transition
                    key={view.id}
                    show={state.activeView.id === view.id}
                    as={Fragment}
                    enter="transition-all ease-in-out duration-700"
                    enterFrom={twx({
                      'opacity-0 translate-x-full': state.lastNavigationAction?.type === 'push',
                      'opacity-0 -translate-x-full': state.lastNavigationAction?.type === 'pop',
                    })}
                    enterTo="opacity-100 translate-x-none"
                    leave="[&>div]:overflow-y-hidden transition-all ease-in-out duration-700"
                    leaveFrom="opacity-100 translate-x-none"
                    leaveTo={twx({
                      'opacity-0 -translate-x-full': state.lastNavigationAction?.type === 'push',
                      'opacity-0 translate-x-full': state.lastNavigationAction?.type === 'pop',
                    })}
                    unmount={false}
                    appear={state.activeView.id === view.id}
                  >
                    <div
                      className={tw`absolute top-0 right-0 bottom-0 left-0 flex h-full max-h-full w-full max-w-full flex-1 flex-col`}
                    >
                      <div
                        data-testid={
                          view.id === state.activeView.id ? 'navigation-stack-body' : undefined
                        }
                        className={tw`scroll-shadows flex-1 overflow-y-auto overflow-x-hidden p-3`}
                      >
                        {view.content.body({ state })}
                      </div>
                      <footer
                        data-testid={
                          view.id === state.activeView.id ? 'navigation-stack-footer' : undefined
                        }
                        className={twx(
                          'z-navigation border-divider-main border-t transition-all duration-300',
                          {
                            'p-2': view.content.footer,
                            hidden: view.content.footer === undefined || footerHeight <= 0,
                          },
                        )}
                      >
                        <div ref={footerContentRef} className={tw`h-full w-full`}>
                          {view.content.footer?.({ state })}
                        </div>
                      </footer>
                    </div>
                  </Transition>
                ))}
              </div>
            </section>
          )}
        </NavigationStackProvider>
      )}
    </TelemetryBoundary>
  );
};

interface NavigationStackState {
  views: NonEmptyArray<NavigationStackView>;
  activeView: NavigationStackView;
  lastNavigationAction: NavigationStackNavigationAction | null;
  previouslyActiveView: NavigationStackView | null;
  pushView: (view: NavigationStackView) => void;
  replaceView: (viewId: string, view: NavigationStackView) => void;
  popView: () => void;
}

const NavigationStackContext = createContext<NavigationStackState | undefined>(undefined);

interface NavigationStackRenderArgs {
  state: NavigationStackState;
}

interface NavigationStackProviderProps {
  initialViews: NonEmptyArray<NavigationStackView>;
  children: (args: NavigationStackRenderArgs) => ReactNode;
}

export const NavigationStackProvider = ({
  children,
  initialViews,
}: NavigationStackProviderProps) => {
  const [views, setViews] = useState<NonEmptyArray<NavigationStackView>>(initialViews);
  const [previouslyActiveView, setPreviouslyActiveView] = useState<NavigationStackView | null>(
    null,
  );
  const [lastNavigationAction, setLastNavigationAction] =
    useState<NavigationStackNavigationAction | null>(null);

  // We know `views.at(-1)` will return a `NavigationStackView` because `views` is a `NonEmptyArray`
  const activeView = useMemo(() => views.at(-1) as NavigationStackView, [views]);

  const state: NavigationStackState = {
    views,
    activeView,
    lastNavigationAction: lastNavigationAction,
    previouslyActiveView,
    pushView: (view) => {
      setPreviouslyActiveView(activeView);
      setLastNavigationAction({ type: 'push', view });
      setViews([...views, view]);
    },
    replaceView: (viewId, view) => {
      setViews(
        views.map((existingView) =>
          existingView.id === viewId ? view : existingView,
        ) as NonEmptyArray<NavigationStackView>,
      );
    },
    popView: (): void => {
      if (views.length > 1) {
        setPreviouslyActiveView(activeView);
        setLastNavigationAction({ type: 'pop', view: activeView });
        // We know that when `views.length > 1`, `views.slice(0, -1)` will return `NonEmptyArray<NavigationStackView>`
        setViews(views.slice(0, -1) as NonEmptyArray<NavigationStackView>);
      }
    },
  };

  return (
    <NavigationStackContext.Provider value={state}>
      {children({ state })}
    </NavigationStackContext.Provider>
  );
};

export const useNavigationStack = () => {
  const context = useContext(NavigationStackContext);

  if (!context) {
    throw new Error('useNavigationStack must be used within a NavigationStackProvider');
  }

  return context;
};

export const useNavigationStackIfAvailable = () => useContext(NavigationStackContext);
